import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {ExtendedAttributes, FaceAttributes, NPC} from "../../server/Plot";
import Typography from "@mui/material/Typography";
import {
    Button,
    ButtonGroup,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField
} from "@mui/material";
import Container from "@mui/material/Container";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

export function NPCCreate() {
    const navigate = useNavigate();
    const [nameError, setNameError] = useState<string | null>(null);
    const [npc, setNPC] = useState<NPC>({
        name: "",
        type: "NPC",
        extended: {} as ExtendedAttributes,
        face_role: {} as FaceAttributes,
    });

    function updateNPC(field: keyof NPC, value: any) {
        setNPC(prev => ({...prev, [field]: value}));
    }

    function updateExtended(field: keyof ExtendedAttributes, value: string) {
        setNPC(prev => ({...prev, extended: {...prev.extended, [field]: value}}));
    }

    function updateFaceRole(field: keyof FaceAttributes, value: string) {
        setNPC(prev => ({...prev, face_role: {...prev.face_role, [field]: value}}));
    }

    async function createNPC() {
        try {
            const {data} = await axios.post("/plot/npcs/", npc)
            navigate('/npc/' + data.id)
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setNameError(err.response.data?.error ?? 'An NPC with this name already exists.')
            }
        }
    }

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" component="h4" align="center">Create NPC</Typography>
                    <Container sx={{textAlign: "center"}}>
                        <ButtonGroup variant="contained" size="small">
                            <Button onClick={createNPC}><SaveIcon/></Button>
                            <Button color="inherit" onClick={() => navigate('/plot')}><CancelIcon/></Button>
                        </ButtonGroup>
                    </Container>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Basics</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcName' label='Name'
                                       value={npc.name}
                                       error={!!nameError}
                                       helperText={nameError ?? ''}
                                       onChange={(ev) => {
                                           updateNPC('name', ev.target.value)
                                           if (nameError) setNameError(null)
                                       }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="type-label">NPC Type</InputLabel>
                                <Select
                                    labelId="type-label"
                                    id="npcType"
                                    value={npc.type}
                                    label="NPC Type"
                                    onChange={(ev) => updateNPC('type', ev.target.value)}
                                >
                                    <MenuItem value={'NPC'}>Named NPC</MenuItem>
                                    <MenuItem value={'FACEROLE'}>Face Role</MenuItem>
                                </Select>
                                <FormHelperText>Select NPC Type</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcPlayedBy' label='Played By'
                                       value={npc.extended?.played_by ?? ""}
                                       onChange={(ev) => updateExtended('played_by', ev.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Appearance</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcSpeciesType' label='Species Type'
                                       value={npc.extended?.species_type ?? ""}
                                       onChange={(ev) => updateExtended('species_type', ev.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcAppearance' label='Outward Appearance'
                                       multiline minRows={4}
                                       value={npc.extended?.appearance ?? ""}
                                       onChange={(ev) => updateExtended('appearance', ev.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Personal Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcPersonality' label='Personality'
                                       multiline minRows={3}
                                       value={npc.extended?.personality ?? ""}
                                       onChange={(ev) => updateExtended('personality', ev.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcNeeds' label='Needs or Intentions'
                                       multiline minRows={3}
                                       value={npc.extended?.needs_intent ?? ""}
                                       onChange={(ev) => updateExtended('needs_intent', ev.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Background</Typography>
                    <Grid item xs={12}>
                        <TextField fullWidth id='npcHistory' label='Background'
                                   multiline minRows={8}
                                   value={npc.extended?.history ?? ""}
                                   onChange={(ev) => updateExtended('history', ev.target.value)}
                        />
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Other Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcGroups' label='Groups'
                                       multiline minRows={3}
                                       value={npc.extended?.groups ?? ""}
                                       onChange={(ev) => updateExtended('groups', ev.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcVirtues' label='Virtues'
                                       multiline minRows={3}
                                       value={npc.face_role?.virtues ?? ""}
                                       onChange={(ev) => updateFaceRole('virtues', ev.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcVices' label='Vices'
                                       multiline minRows={3}
                                       value={npc.face_role?.vices ?? ""}
                                       onChange={(ev) => updateFaceRole('vices', ev.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth id='npcPersonalRelations' label='Personal Relations'
                                       multiline minRows={3}
                                       value={npc.face_role?.personal_relations ?? ""}
                                       onChange={(ev) => updateFaceRole('personal_relations', ev.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    </React.Fragment>
}
